import "swiper/css";
import "swiper/css/thumbs";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useState } from "react";
import { createPortal } from "react-dom";
import GalleryMedia from "./GalleryMedia";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Navigation, Pagination, Thumbs } from "swiper/modules";

type ProjectImage = { url: string };
type Props = { images: ProjectImage[] };
const MAX_VISIBLE = 8;

export default function ProjectGallery({ images }: Props) {
  const [swiperOpen, setSwiperOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);

  const closeSwiper = () => {
    setSwiperOpen(false);
    setThumbsSwiper(null);
  };

  const openSwiper = (index: number) => {
    setStartIndex(index);
    setViewerIndex(index);
    setSwiperOpen(true);
  };

  if (!images?.length) return null;

  const paginationConfig = {
    clickable: true,
    dynamicBullets: true,
    dynamicMainBullets: 3,
  };

  const hiddenCount = Math.max(0, images.length - MAX_VISIBLE);

  const getImageUrl = (img: any) => {
    if (!img) return "";
    if (typeof img === "string") return img;
    if (typeof img.url === "string") return img.url;
    if (typeof img.url === "object" && img.url?.url) return img.url.url;
    return "";
  };

  const slides = images.map(getImageUrl);

  return (
    <>
      {/* GRID */}
      <div className="bg-white xl:shadow-sm p-2.5 xl:rounded-[20px] space-y-2">
        <span className="hidden xl:block text-base font-semibold text-accent-05">
          چند رسانه‌ای
        </span>
        <div className="grid grid-cols-4 gap-2">
          {images.slice(0, MAX_VISIBLE).map((image, i) => {
            const isLast = i === MAX_VISIBLE - 1 && hiddenCount > 0;

            return (
              <div
                key={i}
                className="relative aspect-square border border-green-04/15 rounded-[25px] xl:rounded-[21px] overflow-hidden"
              >
                <GalleryMedia
                  preview
                  src={getImageUrl(image)}
                  alt="تصویر"
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => openSwiper(i)}
                />

                {isLast && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      openSwiper(i);
                    }}
                    className="absolute inset-0 bg-black/60 pointer-events-none flex items-center justify-center text-white text-sm font-bold"
                  >
                    +{hiddenCount}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* FULLSCREEN SWIPER (same pattern as MediaViewer) */}
      {swiperOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 flex flex-col p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeSwiper();
            }}
          >
            <button
              type="button"
              onClick={closeSwiper}
              className="absolute top-4 right-4 pt-1 size-10 rounded-full bg-white/10 text-white text-2xl flex items-center justify-center z-20"
            >
              ×
            </button>

            {/* MAIN SWIPER */}
            <div
              className="flex-1 min-h-0 flex items-center justify-center"
              onClick={(e) => {
                if (e.target === e.currentTarget) closeSwiper();
              }}
            >
              <Swiper
                modules={[Navigation, Pagination, Thumbs]}
                navigation
                thumbs={{ swiper: thumbsSwiper }}
                pagination={images.length > 1 ? paginationConfig : false}
                initialSlide={startIndex}
                onSlideChange={(swiper: SwiperType) => {
                  setViewerIndex(swiper.activeIndex);
                }}
                className="w-full h-full [&_.swiper-pagination]:!bottom-6"
              >
                {slides.map((src, i) => (
                  <SwiperSlide
                    key={i}
                    className="!flex !items-center !justify-center h-full w-full"
                  >
                    <div
                      className="w-full h-full flex items-center justify-center px-2"
                      onClick={(e) => {
                        if (e.target === e.currentTarget) closeSwiper();
                      }}
                    >
                      <GalleryMedia
                        active={viewerIndex === i}
                        src={src}
                        alt="تصویر"
                        className="max-w-full max-h-[90vh] object-contain rounded-2xl select-none"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <style>{`
          .swiper-button-next,
          .swiper-button-prev {
            color: white;
            transform: scale(0.7);
            width: 55px;
            height: 55px;
            padding: 18px;
          }

          .swiper-button-next:after,
          .swiper-button-prev:after {
            font-size: 18px;
          }
        `}</style>
            </div>

            {/* THUMBNAIL CAROUSEL */}
            <div className="h-24 shrink-0 mt-3">
              <Swiper
                onSwiper={setThumbsSwiper}
                spaceBetween={8}
                slidesPerView={6}
                watchSlidesProgress
                slideToClickedSlide
                className="h-full"
              >
                {slides.map((src, i) => (
                  <SwiperSlide key={i} className="!size-20">
                    <GalleryMedia
                      preview
                      src={src}
                      alt="تصویر"
                      className="h-full w-full object-cover opacity-60 hover:opacity-100 cursor-pointer rounded-md"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
